/**
 * Payroll Dashboard — Operational Control Center
 * 
 * The executive view for payroll operations.
 * 
 * Answers:
 * - Is payroll ready?
 * - What is blocking payroll?
 * - Who is affected?
 * - What requires approval?
 * - How much payroll has been processed?
 * - What exports are ready?
 * 
 * Layout:
 * - Executive Summary
 * - Current Pay Period
 * - Payroll Health / Readiness Score
 * - Blocking Issues
 * - Pending Approvals
 * - Employee Payroll Cards
 * - Exports
 * - History
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  DollarSign, Clock, AlertTriangle, CheckCircle, Download,
  FileText, Users, Calendar, ChevronRight, Loader2, RefreshCw,
  Ban, AlertCircle, FileCheck, ExternalLink, Filter, Search, 
  TrendingUp, ArrowRight
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import api from "../../lib/api";

// Readiness ring component
const ReadinessRing = ({ percentage, size = 120, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (percentage >= 90) return "#22c55e"; // green
    if (percentage >= 70) return "#f59e0b"; // yellow
    return "#ef4444"; // red
  };
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-secondary"
        />
        {/* Progress circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
          style={{
            strokeDasharray: circumference,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color: getColor() }}>
          {percentage}%
        </span>
        <span className="text-xs text-muted-foreground">Ready</span>
      </div>
    </div>
  );
};

// Stat card component
const StatCard = ({ label, value, subValue, icon: Icon, trend, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`surface p-4 ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {subValue && <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>}
      </div>
      <div className="p-2 rounded-lg bg-secondary">
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
    </div>
    {trend && (
      <div className="mt-2 flex items-center gap-1 text-xs">
        <TrendingUp className="w-3 h-3 text-green-600" />
        <span className="text-green-600">{trend}</span>
      </div>
    )}
  </motion.div>
);

// Blocking issue card
const BlockingIssueCard = ({ issue, onResolve }) => (
  <div className="p-3 rounded-lg border border-red-200 bg-red-50">
    <div className="flex items-start gap-3">
      <div className="p-1.5 rounded bg-red-100">
        <AlertTriangle className="w-4 h-4 text-red-600" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-red-800">{issue.title}</h4>
        <p className="text-xs text-red-600 mt-0.5">{issue.description}</p>
        <div className="flex items-center gap-2 mt-2">
          <Badge variant="destructive" className="text-[10px]">
            {issue.affected_count} affected
          </Badge>
          <span className="text-xs text-red-600">Est. {issue.estimated_minutes} min to resolve</span>
        </div>
      </div>
      <Button 
        variant="outline" 
        size="sm" 
        className="shrink-0 border-red-300 text-red-700 hover:bg-red-100"
        onClick={() => onResolve(issue)}
      >
        Resolve
        <ChevronRight className="w-3 h-3 ml-1" />
      </Button>
    </div>
  </div>
);

// Employee payroll card
const EmployeePayrollCard = ({ employee, onView }) => (
  <div 
    className="p-3 rounded-lg border bg-card hover:shadow-sm transition-shadow cursor-pointer"
    onClick={() => onView(employee)}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-sm font-medium text-primary">
            {employee.first_name?.[0]}{employee.last_name?.[0]}
          </span>
        </div>
        <div>
          <h4 className="text-sm font-medium">{employee.first_name} {employee.last_name}</h4>
          <p className="text-xs text-muted-foreground">{employee.position || "Caregiver"}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold">${(employee.total_amount || 0).toFixed(2)}</p>
        <p className="text-xs text-muted-foreground">{employee.hours || 0} hrs</p>
      </div>
    </div>
    {employee.status !== "ready" && (
      <div className="mt-2 flex items-center gap-1">
        <AlertCircle className="w-3 h-3 text-yellow-600" />
        <span className="text-xs text-yellow-700">{employee.issue || "Pending review"}</span>
      </div>
    )}
  </div>
);

// Export format button
const ExportButton = ({ format, label, icon: Icon, disabled, onExport }) => (
  <Button
    variant="outline"
    size="sm"
    disabled={disabled}
    onClick={() => onExport(format)}
    className="flex items-center gap-2"
  >
    <Icon className="w-4 h-4" />
    {label}
    <Download className="w-3 h-3 ml-1" />
  </Button>
);

export default function PayrollDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payrollData, setPayrollData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [blockingIssues, setBlockingIssues] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("current");
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const loadPayrollData = useCallback(async () => {
    try {
      // Load payroll summary
      const summaryRes = await api.get("/payroll/summary");
      setPayrollData(summaryRes.data);
      
      // Load employees with payroll data
      const empRes = await api.get("/payroll/employees");
      setEmployees(empRes.data.employees || []);
      
      // Extract blocking issues
      const issues = [];
      if (summaryRes.data.blocking_work_records > 0) {
        issues.push({
          id: "blocking_records",
          title: "Work Records Blocking Payroll",
          description: `${summaryRes.data.blocking_work_records} work record(s) need approval before payroll can be processed`,
          affected_count: summaryRes.data.blocking_work_records,
          estimated_minutes: summaryRes.data.blocking_work_records * 3,
          resolve_url: "/review"
        });
      }
      if (summaryRes.data.missing_signatures > 0) {
        issues.push({
          id: "missing_signatures",
          title: "Missing Signatures",
          description: `${summaryRes.data.missing_signatures} work record(s) are missing required signatures`,
          affected_count: summaryRes.data.missing_signatures,
          estimated_minutes: summaryRes.data.missing_signatures * 2,
          resolve_url: "/review?filter=missing_signature"
        });
      }
      if (summaryRes.data.unresolved_exceptions > 0) {
        issues.push({
          id: "exceptions",
          title: "Unresolved Exceptions",
          description: `${summaryRes.data.unresolved_exceptions} payroll exception(s) need resolution`,
          affected_count: summaryRes.data.unresolved_exceptions,
          estimated_minutes: summaryRes.data.unresolved_exceptions * 5,
          resolve_url: "/exceptions?type=payroll"
        });
      }
      setBlockingIssues(issues);
      
    } catch (err) {
      console.error("Failed to load payroll data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayrollData();
  }, [loadPayrollData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadPayrollData();
    setRefreshing(false);
  };

  const handleResolveIssue = (issue) => {
    navigate(issue.resolve_url);
  };

  const handleExport = async (format) => {
    try {
      // TODO: Implement actual export
      alert(`Exporting payroll in ${format} format...`);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  // Filter employees by search term
  const filteredEmployees = employees.filter(emp => 
    !searchTerm || 
    `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const readinessPercentage = payrollData?.readiness_percentage || 0;
  const totalPayroll = payrollData?.total_amount || 0;
  const totalHours = payrollData?.total_hours || 0;
  const approvedRecords = payrollData?.approved_work_records || 0;
  const pendingRecords = payrollData?.pending_work_records || 0;

  return (
    <div className="px-4 md:px-8 py-6 md:py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="heading-display flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-primary" />
            Payroll Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Operational control center for payroll processing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current Period</SelectItem>
              <SelectItem value="previous">Previous Period</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Executive Summary Row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Readiness Score - Featured */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="lg:col-span-1 surface p-6 flex flex-col items-center justify-center"
        >
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Payroll Readiness</h3>
          <ReadinessRing percentage={readinessPercentage} />
          <div className="mt-4 text-center">
            <p className="text-xs text-muted-foreground">
              {blockingIssues.length > 0 
                ? `${blockingIssues.length} issue(s) blocking` 
                : "All clear for processing"
              }
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Est. completion: <strong>{Math.ceil(blockingIssues.reduce((t, i) => t + i.estimated_minutes, 0) / 60) || "< 1"} hr</strong>
            </p>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Payroll"
            value={`$${totalPayroll.toLocaleString()}`}
            subValue="This period"
            icon={DollarSign}
          />
          <StatCard
            label="Total Hours"
            value={totalHours.toLocaleString()}
            subValue={`${approvedRecords} work records`}
            icon={Clock}
          />
          <StatCard
            label="Approved"
            value={approvedRecords}
            subValue="Ready for export"
            icon={CheckCircle}
          />
          <StatCard
            label="Pending"
            value={pendingRecords}
            subValue="Awaiting action"
            icon={AlertTriangle}
            onClick={() => navigate("/review")}
          />
        </div>
      </div>

      {/* Blocking Issues */}
      {blockingIssues.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-600" />
              Blocking Issues ({blockingIssues.length})
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/review")}>
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid gap-3">
            {blockingIssues.map(issue => (
              <BlockingIssueCard 
                key={issue.id} 
                issue={issue} 
                onResolve={handleResolveIssue}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Exports Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="surface p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export Payroll
          </h2>
          <Badge variant={readinessPercentage >= 100 ? "default" : "secondary"}>
            {readinessPercentage >= 100 ? "Ready to Export" : "Blocked"}
          </Badge>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <ExportButton
            format="csv"
            label="CSV"
            icon={FileText}
            disabled={readinessPercentage < 100}
            onExport={handleExport}
          />
          <ExportButton
            format="adp"
            label="ADP"
            icon={ExternalLink}
            disabled={readinessPercentage < 100}
            onExport={handleExport}
          />
          <ExportButton
            format="quickbooks"
            label="QuickBooks"
            icon={ExternalLink}
            disabled={readinessPercentage < 100}
            onExport={handleExport}
          />
          <ExportButton
            format="paychex"
            label="Paychex"
            icon={ExternalLink}
            disabled={readinessPercentage < 100}
            onExport={handleExport}
          />
          <ExportButton
            format="gusto"
            label="Gusto"
            icon={ExternalLink}
            disabled={readinessPercentage < 100}
            onExport={handleExport}
          />
        </div>
        
        {readinessPercentage < 100 && (
          <p className="text-xs text-muted-foreground mt-3">
            Resolve all blocking issues before exporting payroll.
          </p>
        )}
      </motion.div>

      {/* Employee Payroll Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5" />
            Employee Payroll ({filteredEmployees.length})
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border rounded-lg w-[200px] focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredEmployees.slice(0, 12).map(emp => (
            <EmployeePayrollCard
              key={emp.id}
              employee={emp}
              onView={(e) => navigate(`/employees/${e.id}`)}
            />
          ))}
        </div>
        
        {filteredEmployees.length > 12 && (
          <div className="text-center">
            <Button variant="outline">
              View All {filteredEmployees.length} Employees
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
        
        {filteredEmployees.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>No employees found with payroll data for this period</p>
          </div>
        )}
      </motion.div>

      {/* History Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="surface p-5"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5" />
          Payroll History
        </h2>
        <div className="text-sm text-muted-foreground">
          <p>Previous payroll runs will appear here once processing is complete.</p>
        </div>
      </motion.div>
    </div>
  );
}
